import { Box, Container, SimpleGrid, Stack, Loader, Text, Center } from "@mantine/core";
import TeamProfileCard from "components/TeamProfileCard";
import TeamPlayerList from "./TeamPlayerList";
import TeamStatsSection from "./TeamStatsSection";
import UserMatchFeed, { MatchItem } from "components/profile/UserMatchFeed";
import UserNewFeed from "components/profile/UserNewFeed";
import { useParams } from "react-router-dom";
import { useTeamDetailsQuery, useGetMatchHistoryQuery, PlayerTeam } from "generated/graphql";

/**
 * TeamProfile page (Mantine layout)
 *
 * Responsive 3-row dashboard:
 * - Row 1: TeamProfileCard (left) + TeamPlayerList (right) - stacks on mobile
 * - Row 2: TeamStatsSection (full width)
 * - Row 3: UserMatchFeed (left) + UserNewFeed (right) - stacks on mobile
 *
 * Notes:
 * - On desktop (sm+): Fixed viewport height with grid layout
 * - On mobile: Scrollable content with stacked sections
 */
export default function TeamProfile() {
  // Mantine container outer padding
  const GUTTER = 'md' as const;
  // Read teamId from route to make feeds and links team-relative
  const { teamId } = useParams<{ teamId: string }>();

  // Fetch team details from the API
  // errorPolicy: "all" allows partial data when some fields fail auth (e.g., mmr requires auth)
  const { data: teamData, loading: teamLoading, error: teamError } = useTeamDetailsQuery({
    variables: { teamId: teamId! },
    skip: !teamId,
    errorPolicy: "all",
  });

  // Fetch match history for this team
  // TODO: This query fetches matches where this team is home OR away. Consider
  // adding a dedicated backend resolver for team matches for better performance.
  const { data: matchData, loading: matchLoading } = useGetMatchHistoryQuery({
    variables: {
      where: {
        or: [
          { homeTeamId: { equals: teamId } },
          { awayTeamId: { equals: teamId } },
        ],
      },
      order: [{ scheduledStartTime: 'DESC' as any }],
      take: 10,
    },
    skip: !teamId,
  });

  if (teamLoading) {
    return (
      <Center style={{ height: 'calc(100dvh - 48px)' }}>
        <Loader size="lg" />
      </Center>
    );
  }

  // With errorPolicy: "all", we may have partial data even with errors (e.g., mmr auth failure)
  // Only show error state if there's no team data at all
  if (!teamData?.team) {
    return (
      <Center style={{ height: 'calc(100dvh - 48px)' }}>
        <Text c="red">Failed to load team details.</Text>
      </Center>
    );
  }

  const team = teamData.team;

  // Map roster from API
  const roster = (team.players ?? []).map((p) => ({
    id: p.id,
    name: p.name,
    avatarUrl: p.avatarUrl ?? undefined,
    team: team.name,
    franchise: team.franchise?.name,
    roles: (p.roles ?? []).filter((r): r is string => r !== null),
  }));

  // Calculate team stats from match history
  // TODO: Replace with a dedicated stats service/endpoint when available.
  // Currently aggregating from GameSeries data which may not be complete.
  const matches = matchData?.listGameSeries?.items ?? [];
  // GameSeries status is a string - "Completed", "Scheduled", etc.
  const completedMatches = matches.filter((m) => m.status === 'Completed');

  let wins = 0;
  let losses = 0;
  let draws = 0;
  let roundDiff = 0;

  completedMatches.forEach((match) => {
    const isHome = match.homeTeamId === teamId;
    let teamScore = 0;
    let opponentScore = 0;

    // Sum up game scores
    (match.games ?? []).filter((g): g is NonNullable<typeof g> => g !== null).forEach((game) => {
      const homeScore = game.homeTeamScore ?? 0;
      const awayScore = game.awayTeamScore ?? 0;
      if (isHome) {
        teamScore += homeScore;
        opponentScore += awayScore;
      } else {
        teamScore += awayScore;
        opponentScore += homeScore;
      }
    });

    roundDiff += (teamScore - opponentScore);

    // Determine match outcome based on games won
    // PlayerTeam.TeamA = Home, PlayerTeam.TeamB = Away
    const gamesWon = (match.games ?? []).filter((g): g is NonNullable<typeof g> => g !== null).filter((g) => {
      if (isHome) return g.winningTeam === PlayerTeam.TeamA;
      return g.winningTeam === PlayerTeam.TeamB;
    }).length;
    const gamesLost = (match.games ?? []).filter((g): g is NonNullable<typeof g> => g !== null).filter((g) => {
      if (isHome) return g.winningTeam === PlayerTeam.TeamB;
      return g.winningTeam === PlayerTeam.TeamA;
    }).length;

    if (gamesWon > gamesLost) wins++;
    else if (gamesLost > gamesWon) losses++;
    else draws++;
  });

  const teamStats = {
    matchesPlayed: completedMatches.length,
    wins,
    losses,
    draws,
    roundDiff,
  };

  // Map matches to feed format
  const now = new Date();
  const teamMatches: MatchItem[] = matches.slice(0, 5).map((match) => {
    const isHome = match.homeTeamId === teamId;
    const opponent = isHome ? match.awayTeam?.name : match.homeTeam?.name;
    const scheduledAt = match.scheduledStartTime ?? match.startTime ?? '';
    const isPast = match.status === 'Completed' || (scheduledAt && new Date(scheduledAt) < now);

    // Calculate result for completed matches
    let result: 'W' | 'L' | 'D' | undefined;
    let score: string | undefined;

    if (match.status === 'Completed' && match.games?.length) {
      const validGames = match.games.filter((g): g is NonNullable<typeof g> => g !== null);
      const gamesWon = validGames.filter((g) => {
        if (isHome) return g.winningTeam === PlayerTeam.TeamA;
        return g.winningTeam === PlayerTeam.TeamB;
      }).length;
      const gamesLost = validGames.filter((g) => {
        if (isHome) return g.winningTeam === PlayerTeam.TeamB;
        return g.winningTeam === PlayerTeam.TeamA;
      }).length;

      if (gamesWon > gamesLost) result = 'W';
      else if (gamesLost > gamesWon) result = 'L';
      else result = 'D';

      score = `${gamesWon}-${gamesLost}`;
    }

    return {
      id: match.id,
      opponent: opponent ?? 'Unknown',
      scheduledAt,
      result,
      score,
      tournament: match.tier?.tierName,
      type: isPast ? 'past' : 'upcoming',
    } as MatchItem;
  });

  return (
    <Container fluid p={{ base: "sm", sm: GUTTER }} className="h-auto sm:h-[calc(100dvh-48px)]">
      {/* Mobile: scrollable content; Desktop: fixed viewport height */}
      <div className="flex flex-col gap-3 sm:h-full sm:min-h-0">
        {/* Row 1: Team card + Roster - stacks on mobile */}
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" className="sm:flex-[3] sm:min-h-0">
          <Box className="min-h-[200px] sm:h-full sm:min-h-0">
            <TeamProfileCard
              logoUrl={team.franchise?.logoUrl ?? undefined}
              teamName={team.name}
              franchise={team.franchise?.name}
              tier={team.tier ?? undefined}
              fullHeight
            />
          </Box>
          <Box className="min-h-[250px] sm:h-full sm:min-h-0">
            <TeamPlayerList players={roster} maxHeight={'100%'} />
          </Box>
        </SimpleGrid>

        {/* Row 2: Team stats full width */}
        <Box className="sm:flex-[2] sm:min-h-0">
          <TeamStatsSection stats={teamStats} fullHeight />
        </Box>

        {/* Row 3: Match feed + News feed - stacks on mobile */}
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" className="sm:flex-[6] sm:min-h-0">
          <Box className="min-h-[300px] sm:h-full sm:min-h-0">
            <UserMatchFeed
              title="Team Match Feed"
              items={matchLoading ? [] : teamMatches}
              fullHeight
              moreDetailsTo={`/teams/${teamId}/matches`}
              emptyMessage={matchLoading ? "Loading matches..." : "No matches found."}
            />
          </Box>
          <Box className="min-h-[300px] sm:h-full sm:min-h-0">
            {/* TODO: News feed - no news data source exists yet.
                When a news/announcements feature is implemented, wire this up.
                For now, showing empty state. */}
            <UserNewFeed
              title="Team News Feed"
              items={[]}
              fullHeight
              moreDetailsTo={`/teams/${teamId}/news`}
            />
          </Box>
        </SimpleGrid>
      </div>
    </Container>
  );
}
